import { memo, useCallback, useMemo, useState } from "react";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import type { PatientCommunicationRow } from "../aggregations";
import { buildPatientSendSummary } from "../helpers/buildPatientSendSummary";
import type { OnOpenCommunicationsWhatsAppSend } from "../types/sendMessage";
import { PatientCommunicationsDetailDialog } from "./CommunicationsDetailDialogs";
import CommunicationsPatientSendRankTable from "./CommunicationsPatientSendRankTable";
import { communicationsSubsectionIconBoxSx } from "./communicationsSubsectionStyles";

export interface CommunicationsPatientsFocusPanelProps {
  patientMessageGroups: PatientCommunicationRow[];
  vetApp?: boolean;
  petNamesByTutorLogicalId?: Map<string, string[]>;
  onOpenSendMessage: OnOpenCommunicationsWhatsAppSend;
}

/**
 * Foco em pacientes: KPIs, ranking em tabela e detalhe em dialog.
 */
const CommunicationsPatientsFocusPanel = memo(
  ({
    patientMessageGroups,
    vetApp = false,
    petNamesByTutorLogicalId,
    onOpenSendMessage,
  }: CommunicationsPatientsFocusPanelProps) => {
    const [dialogRow, setDialogRow] = useState<PatientCommunicationRow | null>(
      null,
    );

    const summary = useMemo(
      () => buildPatientSendSummary(patientMessageGroups),
      [patientMessageGroups],
    );

    const closeDialog = useCallback(() => setDialogRow(null), []);

    if (summary.totalSends === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          Nenhum envio registrado ainda.
        </Typography>
      );
    }

    return (
      <Stack spacing={2}>
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(3, minmax(0, 1fr))",
            },
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: "background.paper",
              height: "100%",
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <Paper variant="outlined" sx={communicationsSubsectionIconBoxSx}>
                <SmsOutlinedIcon fontSize="small" />
              </Paper>
              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  Total de envios
                </Typography>
                <Typography variant="subtitle1" fontWeight={700}>
                  {summary.totalSends.toLocaleString("pt-BR")} envios
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: "background.paper",
              height: "100%",
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <Paper variant="outlined" sx={communicationsSubsectionIconBoxSx}>
                <PeopleOutlinedIcon fontSize="small" />
              </Paper>
              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  {vetApp ? "Tutores contactados" : "Pacientes contactados"}
                </Typography>
                <Typography variant="subtitle1" fontWeight={700}>
                  {summary.patientsContactedCount.toLocaleString("pt-BR")}{" "}
                  {vetApp ? "tutores" : "pacientes"}
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: "background.paper",
              height: "100%",
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <Paper variant="outlined" sx={communicationsSubsectionIconBoxSx}>
                <EmojiEventsOutlinedIcon fontSize="small" />
              </Paper>
              <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {vetApp ? "Tutor mais contactado" : "Paciente mais contactado"}
                </Typography>
                <Tooltip title={summary.topPatientLabel ?? ""}>
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {summary.topPatientLabel ?? "—"}
                  </Typography>
                </Tooltip>
                {summary.topPatientLabel ? (
                  <Typography variant="caption" color="text.secondary">
                    {summary.topPatientSendCount.toLocaleString("pt-BR")} envios
                    {" · "}
                    {summary.topPatientSharePercent}% dos envios
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </Paper>
        </Box>

        <CommunicationsPatientSendRankTable
          patientMessageGroups={patientMessageGroups}
          totalSends={summary.totalSends}
          vetApp={vetApp}
          petNamesByTutorLogicalId={petNamesByTutorLogicalId}
          detailPatientId={dialogRow?.patient.patientId ?? null}
          onOpenDetail={setDialogRow}
          onOpenSendMessage={onOpenSendMessage}
        />

        <PatientCommunicationsDetailDialog
          open={dialogRow != null}
          onClose={closeDialog}
          patient={dialogRow?.patient ?? null}
          messages={dialogRow?.messages ?? []}
          vetApp={vetApp}
          petNamesByTutorLogicalId={petNamesByTutorLogicalId}
        />
      </Stack>
    );
  },
);

CommunicationsPatientsFocusPanel.displayName =
  "CommunicationsPatientsFocusPanel";

export default CommunicationsPatientsFocusPanel;
